/** PIAȚA RESURSELOR: tipurile, clientul API și funcțiile pure ale panoului
 *  «Explorează piața» și ale importului prețurilor de piață în proiect.
 *
 *  Prețurile vin din colecția globală a mediului (`planificare/piata`), aduse
 *  de server la forma catalogului listei: lei fără TVA pe unitatea resursei,
 *  franco șantier, fără pompă — iar în EUR cu cursul proiectului. Ecranul nu
 *  recalculează nimic din ele: arată, filtrează și trimite ce a ales omul.
 *
 *  O CHEIE e resursa așa cum o caută colectorul («beton.C30/37»,
 *  «otel.b500c»); ȚINTELE ei sunt prețurile din proiect pe care le schimbă
 *  importul — resurse ale listei (`r:<listă>:<cod>`) sau articole ale
 *  devizelor, pe cheia lor de preț (`a:<cheie_pret>`). Legătura cheie → ținte
 *  o face serverul (`cost_model/piata/chei.py`); aici numai se caută în ea.
 *  Planul: `planificare/docs/plan_piata_resurselor.md`, §4.6 și §6. */
import { get, post } from "../../api";
import { numar, t } from "../../i18n";
import type { Etapa, ListaResurse } from "./boq";

export type Livrare = "franco" | "din_statie" | "franco_cu_pompa" | "din_magazin";

/** Prețul cel mai mic valid al unei chei (lei și EUR, fără TVA, pe bază). */
export type MinimPiata = {
  eur: number; lei: number; sursa: string; sursa_nume: string; vazut_la: string; produs_id: number; nume: string;
};

export type RezumatPiata = { n: number; n_surse: number; minim: MinimPiata; mediana_eur: number; maxim_eur: number };

export type CheiePiata = {
  cheie: string; nume: string; um: string; familie: string;
  /** `null` = colectarea n-a găsit niciun preț valid pentru cheie */
  rezumat: RezumatPiata | null;
};

export type Tinta = {
  id: string; cheie: string; fel: "resursa" | "articol";
  lista?: ListaResurse | string; cod?: string; cheie_pret?: string;
  nume: string; um: string;
  /** prețul de acum în proiect, EUR/UM fără TVA */
  pret_eur: number; sursa: string;
  /** prețul de acum a venit dintr-un import din piață */
  din_piata: boolean;
  /** prețul de acum l-a pus omul (sau o ofertă): importul nu-l rescrie fără bifă */
  pus_de_om: boolean;
  /** faza proiectului (etapa programului): a articolului, la o resursă cea în
   *  care costă cel mai mult (`etape` le are pe toate) */
  etapa?: string | null;
  etape?: (string | null)[];
};

export type Propunere = {
  tinta: string; cheie: string; nume: string; um: string;
  /** faza țintei, ca în `Tinta.etapa` */
  etapa?: string | null;
  pret_eur: number; piata_eur: number; diferenta_pct: number;
  /** eticheta care se scrie pe țintă la import («piață · Evobeton · 31.10.2026 · cel mai mic din 3») */
  sursa: string; sursa_nume: string; vazut_la: string;
  /** bifat implicit în previzualizare */
  bifat: boolean;
  /** de ce nu e bifat sau nu se poate importa («pus_de_om», «fara_curs») */
  motiv: string | null;
};

export type PiataProiect = {
  curs: number | null; colectat_la: string | null;
  chei: CheiePiata[]; tinte: Tinta[]; propunere: Propunere[];
  /** fazele proiectului, în ordinea programului (din lista de cantități) */
  etape?: Etapa[];
};

export type RandPiata = {
  produs_id: number; sursa: string; sursa_nume: string; nivel: 1 | 2 | 3;
  url: string | null; nume: string; furnizor: string | null; autoritate: string | null;
  varianta: Record<string, string | number | boolean>;
  /** produsul ține locul cheii numai aproximativ (altă expunere, marcă veche):
   *  se arată, dar nu intră în «cel mai mic» și nu se poate folosi */
  echivalent: boolean; motiv_echivalent: string | null;
  pret: number; moneda: string; cu_tva: boolean; um_sursa: string | null; cantitate: number | null;
  livrare: Livrare | null;
  /** cât s-a adăugat (transportul) sau scăzut (pompa) ca prețul să ajungă pe baza catalogului */
  ajustare: null | { fel: "transport" | "pompa"; lei: number };
  /** prețul include taxa de acces a orașului? franco = da (în zona
   *  furnizorului), din stație / magazin = nu; lipsește la serverele mai vechi */
  acces?: "inclus" | "neinclus" | null;
  pe_baza_lei: number | null; pe_baza_eur: number | null;
  stare: "ok" | "suspect" | "respins"; motiv: string | null;
  vazut_la: string; locatie: string;
  documente: { fel: string; url: string }[];
  /** rândul e printre prețurile din care se ia «cel mai mic» */
  in_minim: boolean;
};

export type DetaliuCheie = { cheie: CheiePiata; curs: number | null; tinte: Tinta[]; randuri: RandPiata[] };

/** O țintă de pus pe prețul pieței; fără `produs_id`, cel mai mic preț valid. */
export type Aplica = { tinta: string; cheie: string; produs_id?: number | null };

export type RaspunsImport = {
  rev: number;
  aplicate: { tinta: string; nume: string; pret_eur: number; sursa: string }[];
  respinse: { tinta: string; motiv: string }[];
};

// --- API -------------------------------------------------------------------

const cale = (projectId: string) => `/api/projects/${projectId}/piata-resurse`;

/** Cheile cu rezumatul lor, țintele din lista soluției și previzualizarea
 *  importului. O citire de fundal: lipsa ei înseamnă doar că butoanele pieței
 *  nu apar, nu e o veste pentru om. */
export const getPiataResurse = (projectId: string, solutieId: string) =>
  get<PiataProiect>(`${cale(projectId)}?${new URLSearchParams({ solutie: solutieId })}`, { silent: true });

/** Toate prețurile văzute pentru o cheie. Cheia are «/» în ea («beton.C30/37»),
 *  de aceea stă în interogare, nu în cale. */
export const getCheiePiata = (projectId: string, solutieId: string, cheie: string) =>
  get<DetaliuCheie>(`${cale(projectId)}/cheie?${new URLSearchParams({ cheie, solutie: solutieId })}`);

/** Pune prețul pieței pe țintele alese (`aplica`) și scoate-l de pe `revino`. */
export const importaPiata = (projectId: string, solutieId: string, aplica: Aplica[], revino: string[] = []) =>
  post<RaspunsImport>(`${cale(projectId)}/importa`,
                      { solutie: solutieId, aplica, ...(revino.length ? { revino } : {}) });

// --- funcții pure ------------------------------------------------------------

export const idResursa = (lista: string, cod: string) => `r:${lista}:${cod}`;
export const idArticol = (cheiePret: string) => `a:${cheiePret}`;

/** Cheia de piață a unei resurse a listei, dacă are una. */
export function cheiaResursei(tinte: Tinta[] | null | undefined, lista: string, cod: string): string | null {
  const id = idResursa(lista, cod);
  return tinte?.find((x) => x.fel === "resursa" && (x.id === id || (x.lista === lista && x.cod === cod)))?.cheie ?? null;
}

/** Cheia de piață a unui articol de deviz (pe cheia lui de preț), dacă are una. */
export function cheiaArticolului(tinte: Tinta[] | null | undefined, cheiePret: string | null | undefined): string | null {
  if (!cheiePret) return null;
  const id = idArticol(cheiePret);
  return tinte?.find((x) => x.fel === "articol" && (x.id === id || x.cheie_pret === cheiePret))?.cheie ?? null;
}

export const tinteleCheii = (tinte: Tinta[] | null | undefined, cheie: string) =>
  (tinte ?? []).filter((x) => x.cheie === cheie);

/** Eticheta unui preț pus din piață începe așa (`cost_model/piata/importul.py`);
 *  pe un articol, ea deosebește prețul pieței de prețul unei oferte. */
export const PREFIX_PIATA = "piață";
export const dinPiata = (sursa: string | null | undefined) => (sursa ?? "").trim().toLowerCase().startsWith(PREFIX_PIATA);

/** Rândurile arătate: numai potrivirile exacte, sau și echivalentele (după ele).
 *  Ordinea e a prețului pe bază, cele care nu se pot aduce pe bază la urmă —
 *  primul rând e cel pe care l-ar lua «cel mai mic», ca omul să-l vadă întâi. */
export function randuriVazute(randuri: RandPiata[], doarExacte: boolean): RandPiata[] {
  const rang = (r: RandPiata) => (r.echivalent ? 2 : r.stare !== "ok" || r.pe_baza_eur == null ? 1 : 0);
  return randuri
    .filter((r) => !doarExacte || !r.echivalent)
    .map((r, i) => ({ r, i }))
    .sort((a, b) => rang(a.r) - rang(b.r)
      || (a.r.pe_baza_eur ?? Infinity) - (b.r.pe_baza_eur ?? Infinity)
      || a.i - b.i)
    .map((x) => x.r);
}

/** Se poate folosi rândul ca preț al proiectului? Numai un preț exact, valid,
 *  adus pe bază — altfel un preț mic pentru alt produs ar strica tăcut costul. */
export const folosibil = (r: RandPiata) =>
  !r.echivalent && r.stare === "ok" && r.in_minim && r.pe_baza_eur != null;

/** Cererea pentru «Folosește»: toate țintele cheii, pe rândul ales (sau pe
 *  cel mai mic, fără `produsId`). */
export function aplicaPeCheie(tinte: Tinta[], cheie: string, produsId: number | null = null): Aplica[] {
  return tinteleCheii(tinte, cheie).map((x) => ({ tinta: x.id, cheie, ...(produsId != null ? { produs_id: produsId } : {}) }));
}

/** Rândurile previzualizării care se pot importa: un preț fără cursul
 *  proiectului n-are cum ajunge în EUR. */
export const importabila = (p: Propunere) => p.motiv !== "fara_curs" && Number.isFinite(p.piata_eur) && p.piata_eur > 0;

export const bifateImplicit = (propunere: Propunere[]) =>
  new Set(propunere.filter((p) => p.bifat && importabila(p)).map((p) => p.tinta));

/** Cererea importului din previzualizare: rândurile bifate, pe cel mai mic preț. */
export const aplicaDinPropunere = (propunere: Propunere[], bifate: Set<string>): Aplica[] =>
  propunere.filter((p) => bifate.has(p.tinta) && importabila(p)).map((p) => ({ tinta: p.tinta, cheie: p.cheie }));

const UM: Record<string, string> = { m2: "m²", m3: "m³" };
export const umPiata = (u: string | null | undefined) => (u ? UM[u] ?? u : "");

/** Cum a ajuns prețul pe baza catalogului: livrarea sursei și ajustarea făcută
 *  («din stație · +transport 25 lei/m³»). Ajustarea e o ipoteză a colectorului,
 *  de aceea se scrie pe rând, nu se ascunde în cifră. */
export function textBaza(r: Pick<RandPiata, "livrare" | "ajustare" | "acces">, um: string): string {
  const parti: string[] = [];
  if (r.livrare) parti.push(t(`piata_res.livrare.${r.livrare}`));
  if (r.ajustare) {
    parti.push(t(`piata_res.ajustare.${r.ajustare.fel}`,
                 { v: numar(r.ajustare.lei, { maximumFractionDigits: 2 }), um: umPiata(um) }));
  }
  // taxa de acces a orașului: devizul o pune separat, pe zona lotului, deci
  // omul trebuie să vadă dacă oferta o conține deja (`cost_model.acces`)
  if (r.acces) parti.push(t(`piata_res.acces.${r.acces}`));
  return parti.join(" · ");
}

/** Specificația văzută pe produs, scurt: «S3 · D16 · XC2», «Ø12 · 12 m». */
export function textVarianta(v: Record<string, string | number | boolean> | null | undefined): string {
  if (!v) return "";
  const parti: string[] = [];
  for (const [k, x] of Object.entries(v)) {
    if (x === false || x == null || x === "") continue;
    if (k === "diametru_mm") parti.push(`Ø${x}`);
    else if (k === "lungime_m") parti.push(`${x} m`);
    else if (k === "dmax_mm") parti.push(`D${x}`);
    else if (x === true) parti.push(k);
    else parti.push(String(x));
  }
  return parti.join(" · ");
}
